import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Message, SidebarButton, SidebarHeaderTitle, SidebarTitle } from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { SidebarMobileActions } from 'screens/MakePayment/components';
import { ChangeLogAction, ChangeLogEntity, CheckModifiedDetails, ICheck, ITipCalculatorChangeLogEntry } from 'types';
import { sortAlphaNumerically } from 'utils';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForUILongFormat } from 'utils/date';

interface ISummaryProps {
  className?: string;
  date: string;
  isLoading: boolean;
  primaryActionBtn?: (device: 'mobile' | 'desktop', allowRecalculate: boolean) => React.ReactNode;
  secondaryActionBtn?: (device: 'mobile' | 'desktop') => React.ReactNode;
  totalTipsReceived: number;
  sidebarOpen?: boolean;
  ignoredChecks?: ICheck[];
  splitChecks?: ICheck[];
  changeLogEntries?: ITipCalculatorChangeLogEntry[];
  onExpand?: () => void;
}

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${toRem(40)};

  & > button {
    flex-grow: 1;
  }

  & > * {
    &:not(:last-child) {
      margin-right: ${toRem(16)};
    }
  }
`;

const HeaderText = styled.p`
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.medium};
  word-wrap: break-word;
  max-width: 100%;
`;

const SummaryLayout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  padding: ${toRem(16)} ${toRem(40)};
  background: ${theme.color.neutral[50]};
  border-left: 1px solid ${theme.color.neutral[300]};

  @media (min-width: ${theme.breakpoint.large}px) {
    flex-grow: 1;
    padding: ${toRem(40)};
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${toRem(24)};

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const PaymentDate = styled.p`
  margin-bottom: ${toRem(8)};
  font-size: ${toRem(20)};
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &:not(:last-child) {
    margin-bottom: ${toRem(8)};
  }
`;

const OverrideCheckSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${toRem(16)};
`;

const OverrideCheckList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${toRem(8)};
`;

const OverrideCheckListRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  &:not(:first-child) {
    margin-top: ${toRem(8)};
  }
`;

const OverrideCheckListItem = styled.div`
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(14)};
  padding: ${toRem(4)} 0;
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${toRem(24)};
`;

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Summary: React.FC<ISummaryProps> = ({ className = '', date, primaryActionBtn, secondaryActionBtn, totalTipsReceived = 0, sidebarOpen, onExpand, isLoading, ignoredChecks, splitChecks, changeLogEntries = [] }) => {
  const ignoredChecksTotal = ignoredChecks?.reduce((acc, check) => acc + check.totals.net, 0) || 0;
  const tipsToBePaid = totalTipsReceived - ignoredChecksTotal;
  const allChecksIgnored = ignoredChecksTotal >= totalTipsReceived;
  const cashEditChangeLog = changeLogEntries.filter((item) => item.entity === ChangeLogEntity.CHECK && item.action === ChangeLogAction.CASH_EDITED).sort(sortAlphaNumerically('details.displayId'));
  const ignoredTotal = ignoredChecks?.reduce((acc, check) => acc + check.totals.gross, 0) || 0;

  const renderErrors = useMemo(() => {
    if (tipsToBePaid <= 0 && totalTipsReceived <= 0) {
      return <Message dataCy="no-tips-error" message="There are currently no collected tips or auto-gratuities to be paid." appearance={Appearance.WARNING} />;
    }

    if (allChecksIgnored) {
      return <Message dataCy="ignore-checks-error" message="All checks have been ignored. Please select “Undo Ignore” on at least one check in order to proceed with making a payment." appearance={Appearance.WARNING} />;
    }

    return <></>;
  }, [tipsToBePaid, totalTipsReceived, allChecksIgnored]);

  return (
    <SummaryLayout className={className} data-cy="checks-summary">
      <MobileHeader>
        <SidebarHeaderTitle>
          <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
          <SidebarTitle title="Checks Summary" />
        </SidebarHeaderTitle>
        <SidebarMobileActions columns={2}>
          {secondaryActionBtn('mobile')}
          {primaryActionBtn('mobile', tipsToBePaid > 0)}
        </SidebarMobileActions>
      </MobileHeader>
      <SummaryWrapper>
        <PaymentDate data-cy="checks-payment-date">{formatDateForUILongFormat(date)}</PaymentDate>
        <SummarySection>
          <Row>
            <HeaderText data-cy="collected-tips-header">Collected Tips & Autograts</HeaderText>
            <HeaderText data-cy="collected-tips-amount">{isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(totalTipsReceived)}</HeaderText>
          </Row>
          {ignoredChecks?.length > 0 && (
            <>
              <Row>
                <HeaderText data-cy="ignored-tips-header">Ignored</HeaderText>
                <HeaderText data-cy="ignored-tips-amount">{isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(ignoredTotal)}</HeaderText>
              </Row>
              <Row>
                <HeaderText data-cy="to-be-paid-tips-header">Tips To Be Paid</HeaderText>
                <HeaderText data-cy="to-be-paid-tips-amount">{isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(totalTipsReceived - ignoredTotal)}</HeaderText>
              </Row>
            </>
          )}
          {splitChecks?.length > 0 && (
            <OverrideCheckSection>
              <HeaderText>Split Checks</HeaderText>
              <OverrideCheckList>
                {splitChecks.map((splitCheck, index) => (
                  <OverrideCheckListRow key={`split-check-${index}`}>
                    <OverrideCheckListItem>#{splitCheck.displayId}</OverrideCheckListItem>
                    <OverrideCheckListItem>{centsToDollarsDisplay(splitCheck.totals.gross)}</OverrideCheckListItem>
                  </OverrideCheckListRow>
                ))}
              </OverrideCheckList>
            </OverrideCheckSection>
          )}
          {ignoredChecks?.length > 0 && (
            <OverrideCheckSection>
              <HeaderText>Ignored Checks</HeaderText>
              <OverrideCheckList>
                {ignoredChecks.map((ignoredCheck, index) => (
                  <OverrideCheckListRow key={`ignored-check-${index}`}>
                    <OverrideCheckListItem>#{ignoredCheck.displayId}</OverrideCheckListItem>
                    <OverrideCheckListItem>{centsToDollarsDisplay(ignoredCheck.totals.gross)}</OverrideCheckListItem>
                  </OverrideCheckListRow>
                ))}
              </OverrideCheckList>
            </OverrideCheckSection>
          )}
          {cashEditChangeLog?.length > 0 && (
            <OverrideCheckSection>
              <HeaderText>Cash Tips Updates</HeaderText>
              <OverrideCheckList>
                {cashEditChangeLog.map((item, index) => (
                  <OverrideCheckListRow key={`check-cash-edit-${index}`}>
                    <OverrideCheckListItem>#{(item.details as CheckModifiedDetails)?.displayId}</OverrideCheckListItem>
                    <OverrideCheckListItem>{centsToDollarsDisplay((item.details as CheckModifiedDetails)?.to)}</OverrideCheckListItem>
                  </OverrideCheckListRow>
                ))}
              </OverrideCheckList>
            </OverrideCheckSection>
          )}
        </SummarySection>
        {renderErrors}
      </SummaryWrapper>
      <ButtonsWrapper>
        {secondaryActionBtn('desktop')}
        {primaryActionBtn('desktop', tipsToBePaid > 0)}
      </ButtonsWrapper>
    </SummaryLayout>
  );
};

export default Summary;
